/*
 * @providesModule GDPRCheckbox
 *
 */

import React from 'react';

import ClientPropertyConstants from 'ClientPropertyConstants';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import Label from 'Label';
import Optional from 'Optional';
import PropTypes from 'prop-types';
import styles from 'GDPRCheckboxStyles';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';

import {TouchableHighlight, View} from 'react-native';

const {checkMarkColor, viewSecondaryColor} = SkillzStyle.fullStyles;

export default class GDPRCheckbox extends React.Component {
  static propTypes = {
    testID: PropTypes.string,
    preChecked: PropTypes.bool,
    additionalStyles: PropTypes.object,
    textStyle: PropTypes.object,
    backgroundColor: PropTypes.string,
    replaceContainerStyle: PropTypes.bool,
    boxBorderColor: PropTypes.string,
    boxFillColorIfChecked: PropTypes.string,
    boxFillColorIfUnchecked: PropTypes.string,
    checkMarkColor: PropTypes.string,
    callback: PropTypes.func,
  };

  static defaultProps = {
    boxFillColorIfChecked: 'transparent',
    boxFillColorIfUnchecked: 'transparent',
  };

  constructor(props: Object) {
    super(props);
    this.state = {
      isChecked: this._shouldBeCheckedByDefault(props),
    };
    this.toggleChecked = this._toggleChecked.bind(this);
    this._addListeners();
  }

  componentDidMount() {
    this._informParentOfBoxStatus();
  }

  componentDidUpdate() {
    this._informParentOfBoxStatus();
  }

  componentWillUnmount() {
    this._removeListeners();
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];

    this.subscriptions.push(
      GlobalEventEmitter.addListener('checkChanged', (changedTo: boolean) => {
        if (changedTo != this.isChecked()) {
          this.setState({
            isChecked: changedTo,
          });
        }
      })
    );
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: unknown): void => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
  }

  _informParentOfBoxStatus() {
    if (this.props.callback) {
      this.props.callback(this.isChecked());
    }
  }

  _getBackgroundFromProps(): Object {
    return this.props.backgroundColor ? {backgroundColor: this.props.backgroundColor} : null;
  }

  isChecked(): boolean {
    return this.state.isChecked;
  }

  _shouldBeCheckedByDefault(props: unknown): boolean {
    return props.preChecked != null && typeof props.preChecked != 'undefined'
      ? props.preChecked
      : !ClientPropertyConstants.getIsGDPREnabled();
  }

  _toggleChecked() {
    this.setState(
      {
        isChecked: !this.state.isChecked,
      },
      () => {
        //synchronize step to ensure all rendered checkboxes obey this change
        GlobalEventEmitter.emit('checkChanged', this.state.isChecked);
      }
    );
  }

  _renderCheckBox(): React.ReactNode {
    return (
      <View style={styles.checkboxContainer}>
        <Optional test={this.state.isChecked}>{this._renderCheckmark()}</Optional>
        {this._renderCheckmarkBox()}
      </View>
    );
  }

  _renderCheckmarkBox(): React.ReactNode {
    const borderColor = this.props.boxBorderColor
      ? this.props.boxBorderColor
      : viewSecondaryColor.backgroundColor;
    return (
      <TouchableHighlight
        testID={this.props.testID}
        onPress={this.toggleChecked}
        accessibilityStates={this.isChecked() ? ['selected'] : []}
        style={{margin: 10}}
        hitSlop={{top: 10, left: 10, bottom: 10, right: 10}}
        underlayColor={'transparent'}
        accessible={true}
        accessibilityRole={'button'}
        accessibilityLabel={
          this.isChecked() ? I18n.message('Box checked') : I18n.message('Box unchecked')
        }
      >
        <View>
          <SkillzImage
            source={'ic_check_box_unchecked_skz.png'}
            resizeMode={'contain'}
            color={borderColor}
            style={styles.checkBox}
          />
          <View
            style={[
              styles.checkBoxFill,
              {
                backgroundColor: this.isChecked()
                  ? this.props.boxFillColorIfChecked
                  : this.props.boxFillColorIfUnchecked,
              },
            ]}
          />
        </View>
      </TouchableHighlight>
    );
  }

  _renderCheckmark(): React.ReactNode {
    const checkColor = this.props.checkMarkColor
      ? this.props.checkMarkColor
      : checkMarkColor.backgroundColor;
    return (
      <SkillzImage
        pointerEvents={'none'}
        source={'ic_checkmark_green_skz.png'}
        color={checkColor}
        resizeMode={'contain'}
        style={styles.checkmark}
      />
    );
  }

  _renderText(): React.ReactNode {
    return (
      <View style={styles.textContainer}>
        <Label style={[styles.textStyle, this.props.textStyle]} numberOfLines={2}>
          {ClientPropertyConstants.getGDPROptInLanguage()}
        </Label>
      </View>
    );
  }

  render(): React.ReactNode {
    const containerStyle = [
      styles.mainContainer,
      this.props.additionalStyles,
      this._getBackgroundFromProps(),
    ];
    if (this.props.replaceContainerStyle) {
      containerStyle.shift();
    }
    return (
      <View style={containerStyle}>
        {this._renderCheckBox()}
        {this._renderText()}
      </View>
    );
  }
}
